import request from './request'

/**
 * 取得報名表單資料。
 *
 * @param {Object} params - 請求的參數。
 * @param {string} params.registeredEmail - 使用者電子郵件。
 * @param {string} params.applyDepartment - 申請科系。
 * @return {Promise} 表示請求的 Promise 物件。
 */
function getRegistrationForm(params) {
  return request({
    url: '/registrationForm/getRegistrationForm',
    method: 'get',
    params,
  })
}

/**
 * 提交報名表單。
 * @typedef {Object} RegistrationForm
 * @param {Object} params - 請求的參數。
 * @param {string} params.registeredEmail - 使用者電子郵件。
 * @param {string} params.applyDepartment - 申請科系。
 * @param {Object} data - 要提交的表單資料。
 * @property {Object} data.examinee - 考生資料。
 * @property {number} data.examinee.id - 考生的ID。
 * @property {boolean} data.examinee.consent - 同意報名表單的布林值。
 * @property {string} data.examinee.registeredEmail - 考生的電子郵件。
 * @property {string} data.examinee.lastName - 姓。
 * @property {string} data.examinee.firstName - 名。
 * @property {Object} data.examinee.idPhoto - 證件照。
 * @property {string} data.examinee.idPhoto.name - 證件照名稱。
 * @property {string} data.examinee.idPhoto.url - 證件照位址。
 * @property {Object} data.examinee.idPhoto.uid - 證件照 ID。
 * @property {string} data.examinee.idPhoto.fileID - 證件照檔案 ID。
 * @property {string} data.examinee.idPhoto.fileName - 證件照檔案名稱。
 * @property {string} data.examinee.idPhoto.fileSize - 證件照檔案大小。
 * @property {string} data.examinee.idPhoto.saveTime - 證件照檔案儲存時間。
 * @property {string} data.examinee.idCardNum - 身分證號碼。
 * @property {string} data.examinee.gender - 性別。
 * @property {string} data.examinee.dateOfBirth - 出生日期。
 * @property {string} data.examinee.telephone - 電話。
 * @property {string} data.examinee.cellphoneCode - 手機國碼。
 * @property {string} data.examinee.cellphone - 手機號碼。
 * @property {string} data.examinee.nationality - 國籍。
 * @property {string} data.examinee.permanentCode - 戶籍地郵遞區號。
 * @property {string} data.examinee.permanentAddress - 戶籍地址。
 * @property {string} data.examinee.residentialCode - 通訊地郵遞區號。
 * @property {string} data.examinee.residentialAddress - 通訊地址。
 * @property {string} data.examinee.emergContName - 緊急聯絡人姓名。
 * @property {string} data.examinee.emergContRelation - 緊急聯絡人關係。
 * @property {string} data.examinee.emergContNo - 緊急聯絡人電話。
 * @property {string} data.examinee.examineeNum - 報名編號。
 * @property {Object} data.entryForm - 報名表單資料。
 * @property {number} data.entryForm.id - 報名表單的ID。
 * @property {string} data.entryForm.applyDepartment - 申請科系。
 * @property {string} data.entryForm.generalOrOther - 報名科系。
 * @property {string} data.entryForm.education - 學歷。
 * @property {string} data.entryForm.eduLevel - 學歷等級。
 * @property {string} data.entryForm.gradSchool - 畢業學校。
 * @property {string} data.entryForm.gradDepartment - 畢業科系。
 * @property {string} data.entryForm.equivalentEdu - 等價學歷。
 * @property {string} data.entryForm.gradYear - 畢業年份。
 * @property {string} data.entryForm.gradMonth - 畢業月份。
 * @property {Object} data.entryForm.eduDoc - 學歷文件。
 * @property {string} data.entryForm.eduDoc.name - 學歷文件名稱。
 * @property {string} data.entryForm.eduDoc.url - 學歷文件網址。
 * @property {string} data.entryForm.eduDoc.uid - 學歷文件ID。
 * @property {string} data.entryForm.eduDoc.fileID - 學歷文件ID。
 * @property {string} data.entryForm.eduDoc.fileName - 學歷文件名稱。
 * @property {number} data.entryForm.eduDoc.fileSize - 學歷文件大小。
 * @property {string} data.entryForm.eduDoc.saveTime - 學歷文件上傳時間。
 * @property {string} data.entryForm.experienceDoc.name - 經歷文件名稱。
 * @property {string} data.entryForm.experienceDoc.url - 經歷文件網址。
 * @property {string} data.entryForm.experienceDoc.uid - 經歷文件ID。
 * @property {string} data.entryForm.experienceDoc.fileID - 經歷文件ID。
 * @property {string} data.entryForm.experienceDoc.fileName - 經歷文件名稱。
 * @property {number} data.entryForm.experienceDoc.fileSize - 經歷文件大小。
 * @property {number} data.entryForm.experienceDoc.saveTime - 經歷文件上傳時間。
 * @property {string} data.entryForm.studyPlanDoc.name - 學習計畫文件名稱。
 * @property {string} data.entryForm.studyPlanDoc.url - 學習計畫文件網址。
 * @property {string} data.entryForm.studyPlanDoc.uid - 學習計畫文件ID。
 * @property {string} data.entryForm.studyPlanDoc.fileID - 學習計畫文件ID。
 * @property {string} data.entryForm.studyPlanDoc.fileName - 學習計畫文件名稱。
 * @property {number} data.entryForm.studyPlanDoc.fileSize - 學習計畫文件大小。
 * @property {number} data.entryForm.studyPlanDoc.saveTime - 學習計畫文件上傳時間。
 * @property {string} data.entryForm.certificateDoc.name - 專業領域證明文件名稱。
 * @property {string} data.entryForm.certificateDoc.url - 專業領域證明文件網址。
 * @property {string} data.entryForm.certificateDoc.uid - 專業領域證明文件ID。
 * @property {string} data.entryForm.certificateDoc.fileID - 專業領域證明文件ID。
 * @property {string} data.entryForm.certificateDoc.fileName - 專業領域證明文件名稱。
 * @property {number} data.entryForm.certificateDoc.fileSize - 專業領域證明文件大小。
 * @property {number} data.entryForm.certificateDoc.saveTime - 專業領域證明文件上傳時間。
 * @property {string} data.entryForm.entryFormNum - 報名編號。
 * @property {string} data.entryForm.examStatus - 考試狀態。
 * @property {string} data.entryForm.feePhaseOneStatus - 第一階段費用狀態。
 * @property {string} data.entryForm.feePhaseTwoStatus - 第二階段費用狀態。
 * @returns {Promise} axios的Promise物件
 */
function submitRegistrationForm(params, data) {
  return request({
    url: '/registrationForm/submitRegistrationForm',
    method: 'post',
    params,
    data,
  })
}

/**
 * 更新報名表單。
 * @type {RegistrationForm}
 * @see {@link RegistrationForm}
 * @param {Object} params - 請求的參數。
 * @param {string} params.registeredEmail - 使用者電子郵件。
 * @param {string} params.applyDepartment - 申請科系。
 * @param {Object} data - 要提交的表單資料。
 */
function updateRegistrationForm(params, data) {
  return request({
    url: '/registrationForm/updateRegistrationForm',
    method: 'post',
    params,
    data,
  })
}

/**
 * 取得報名表單付款狀態。
 *
 * @param {Object} params - 請求的參數。
 * @param {string} params.registeredEmail - 使用者電子郵件。
 * @param {string} params.applyDepartment - 申請科系。
 * @param {number} params.entryFormId - 報名單編號。
 * @returns {Promise} axios的Promise物件
 */
function getEntryFormPaymentStatus(params) {
  return request({
    url: '/registrationForm/getEntryFormPaymentStatus',
    method: 'get',
    params,
  })
}

export default {
  getRegistrationForm,
  submitRegistrationForm,
  updateRegistrationForm,
  getEntryFormPaymentStatus,
}
